import React from 'react';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Container, Heading, HtmlContent } from './ui';
import questionIcon from '../images/question-icon.svg';

const StyledFaqs = styled.section`
  background-color: ${({ alt }) =>
    alt ? `${standardColours.lightestGrey}` : `${brandColours.secondary}`};
`;

const StyledInner = styled.div`
  position: relative;
  ${sectionPaddings('45px', '90px')};
`;

const StyledContainer = styled(Container)`
  padding-right: 0;
  padding-left: 0;
`;

const StyledHeading = styled(Heading)`
  text-align: center;
  margin-bottom: 20px;
  color: ${({ alt }) => (alt ? brandColours.secondary : standardColours.white)};

  ${minBreakpointQuery.small`
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 40px;
  `}
`;

const StyledItem = styled.article`
  background: url(${questionIcon}) no-repeat, ${standardColours.white};
  background-size: 32px 32px;
  margin: 15px 0;
  border-radius: 30px;
  padding: 82px 30px 30px 30px;
  background-position: 30px 30px;

  ${minBreakpointQuery.tiny`
    padding: 87px 40px 35px 40px;
    background-position: 35px 30px;
  `}

  ${minBreakpointQuery.small`
    padding: 92px 60px 40px 60px;
    background-position: 40px 30px;
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 40px;
    background-position: 16px 40px;
  `}

  ${minBreakpointQuery.medium`
    padding: 45px 80px;
    background-position: 24px 45px;
  `}

  ${minBreakpointQuery.large`
    padding: 50px 100px;
    background-position: 48px 52px;
  `}
`;

const StyledSubHeading = styled.h3`
  ${fontSize(20)};
  line-height: 1.5;

  ${minBreakpointQuery.small`
    ${fontSize(22)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}
`;

const StyledContent = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledImage = styled.img`
  height: 120px;
  width: 120px;

  ${maxBreakpointQuery.xlarge`
    display: block;
    margin-top: -60px;
    margin-left: auto;
  `}

  ${maxBreakpointQuery.large`
    margin-top: -55px;
  `}

  ${maxBreakpointQuery.medium`
    margin-top: -50px;
  `}

  ${maxBreakpointQuery.small`
    margin-top: -45px;
  `}

  ${maxBreakpointQuery.tiny`
    margin-top: -40px;
  `}

  ${minBreakpointQuery.tiny`
    height: 180px;
    width: 180px;
  `}

  ${minBreakpointQuery.small`
    height: 240px;
    width: 240px;
  `}

  ${minBreakpointQuery.xlarge`
    position: absolute;
    right: 0;
    bottom: -20px;
    height: 180px;
    width: 180px;
  `}

  ${minBreakpointQuery.xxlarge`
    bottom: -40px;
    height: 240px;
    width: 240px;
  `}

  ${minBreakpointQuery.xxxlarge`
    bottom: -60px;
    height: 300px;
    width: 300px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    bottom: -80px;
    height: 360px;
    width: 360px;
  `}
`;

const Faqs = ({ heading, items, image, alt }) => (
  <>
    <HelmetDatoCms
      script={[
        {
          type: 'application/ld+json',
          innerHTML: `{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  ${items.map(({ heading, content }) =>
                    JSON.stringify({
                      '@type': 'Question',
                      name: heading,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: content,
                      },
                    })
                  )}
                ]
              }`,
        },
      ]}
    />

    <StyledFaqs alt={alt}>
      <Container wide={true}>
        <StyledInner>
          <StyledContainer narrow={true}>
            <StyledHeading alt={alt}>{heading}</StyledHeading>
            {items.map(({ heading, content }, id) => {
              return (
                <StyledItem key={id}>
                  <StyledSubHeading>{heading}</StyledSubHeading>
                  <StyledContent content={content} />
                </StyledItem>
              );
            })}
          </StyledContainer>
          {image && (
            <StyledImage src={image.url} alt={image.alt} loading="lazy" />
          )}
        </StyledInner>
      </Container>
    </StyledFaqs>
  </>
);

export default Faqs;
